<template>
  <v-client-table :data="items" :columns="columns" :options="options" class="table-default funding-tours">
    <template #h__select>
      <app-checkbox
        v-if="selectableItems.length"
        :value="allSelected"
        label="Select all tours on this page"
        single
        @change="$emit('select-all', $event)"
      />
    </template>
    <template #select="{ row }">
      <app-checkbox
        v-if="canSelect(row) && !excluded.includes(groupId(row.group))"
        :value="selected.includes(groupId(row.group))"
        :label="`Select ${groupName(row.group)}`"
        single
        @change="$emit('select', row, $event)"
      />
    </template>
    <template #h__group><div class="table-default__left">Tour</div></template>
    <template #group="{ row }">
      <div class="table-default__left">
        {{ groupName(row.group) }}
        <template v-if="row.bookings && row.bookings.length">
          <div v-if="!detail">Full permit: {{ formatAmount(row.current_permit_total, 'USD') }} · Unmapped: {{ formatAmount(row.unmapped_need, 'USD') }}</div>
          <funding-bookings :bookings="row.bookings" :input-prefix="'queue-booking-' + groupId(row.group)" :editable="selectable" />
        </template>
      </div>
    </template>
    <template #first_activity="{ row }">{{ formatDate(row.first_activity) }}</template>
    <template #current_funding_base="{ row }">{{ formatAmount(row.current_funding_base, row.currency) }}</template>
    <template #current_permit_total="{ row }">{{ formatAmount(row.current_permit_total, row.currency) }}</template>
    <template #funding_remaining_amount="{ row }">{{ formatAmount(row.funding_remaining_amount, 'USD') }}</template>
    <template #coverage="{ row }">
      <div v-if="row.coverage" class="table-default__left funding-tours__coverage">
        <div>Paid: {{ formatAmount(row.coverage.paid_amount_usd, 'USD') }}</div>
        <div>Committed: {{ formatAmount(row.coverage.committed_usd, 'USD') }}</div>
        <div>Reserved: {{ formatAmount(row.coverage.reserved_usd, 'USD') }}</div>
        <div>Free: {{ formatAmount(row.coverage.free_usd, 'USD') }}</div>
      </div>
      <span v-else>—</span>
    </template>
    <template #funding_base_snapshot="{ row }">{{ formatAmount(row.funding_base_snapshot, row.currency) }}</template>
    <template #funded_before_snapshot="{ row }">{{ formatAmount(row.funded_before_snapshot, row.currency) }}</template>
    <template #confirmed_funding_amount="{ row }">{{ formatAmount(row.confirmed_funding_amount, row.currency) }}</template>
    <template #amount="{ row }">{{ formatAmount(detail ? row.funding_amount : row.available_funding_amount, row.currency) }}</template>
    <template #h__availability><div class="table-default__left">Availability</div></template>
    <template #availability="{ row }">
      <div class="table-default__left">
        <span
          class="status-tag status-tag--small funding-tours__availability"
          :class="[`funding-tours__availability--${row.availability}`, `funding-tours__availability--${row.unavailable_reason}`]"
        >{{ availabilityLabel(row) }}</span>
        <div v-if="availabilityReason(row)" class="funding-tours__reason">{{ availabilityReason(row) }}</div>
      </div>
    </template>
  </v-client-table>
</template>

<script>
import FundingBookings from './Bookings'
import { availabilityLabel, availabilityReason, canSelect, formatAmount, formatDate, groupId, groupName, tableOptions } from './funding'

export default {
  name: 'FundingToursTable',
  components: { FundingBookings },
  props: {
    items: { type: Array, required: true },
    selected: { type: Array, default: () => [] },
    excluded: { type: Array, default: () => [] },
    coverage: Boolean,
    selectable: Boolean,
    detail: Boolean,
  },
  computed: {
    columns() {
      return [
        ...(this.selectable ? ['select'] : []),
        'group',
        'first_activity',
        ...(this.selectable ? [this.coverage ? 'current_funding_base' : 'current_permit_total', 'confirmed_funding_amount'] : []),
        ...(this.selectable && this.coverage ? ['funding_remaining_amount', 'coverage'] : []),
        ...(this.detail ? ['funding_base_snapshot', 'funded_before_snapshot'] : []),
        'amount',
        ...(this.selectable ? ['availability'] : []),
      ]
    },
    options() {
      return tableOptions({
        select: '',
        group: 'Tour',
        first_activity: 'First activity',
        current_funding_base: 'Funding requirement',
        current_permit_total: 'Permit total',
        funding_remaining_amount: 'Remaining requirement',
        coverage: 'Payer coverage',
        funding_base_snapshot: 'Saved requirement',
        funded_before_snapshot: 'Previously funded',
        confirmed_funding_amount: 'Funded',
        amount: this.selectable ? 'Available funding' : 'Funding amount',
        availability: 'Availability',
      }, Math.max(this.items.length, 1))
    },
    selectableItems() {
      return this.items.filter(row => canSelect(row) && !this.excluded.includes(groupId(row.group)))
    },
    allSelected() {
      return this.selectableItems.length > 0 && this.selectableItems.every(row => this.selected.includes(groupId(row.group)))
    },
  },
  methods: { availabilityLabel, availabilityReason, canSelect, formatAmount, formatDate, groupId, groupName },
}
</script>

<style lang="sass" scoped>
.funding-tours ::v-deep .checkbox
  vertical-align: middle

.funding-tours ::v-deep td:first-child > .checkbox .checkbox__text
  +visually-hidden

.funding-tours__availability
  border-radius: 4px
  font-weight: 400
  color: #a15c00
  background: #fff1dc

  &--fully_funded, &--reserved_in_draft
    color: $color-text-second
    background: rgba($color-text-second, .1)

  &--available
    color: darken($color-theme, 15%)
    background: rgba($color-theme, .14)

.funding-tours__coverage
  min-width: 180px
  line-height: 1.5

.funding-tours__reason
  margin-top: 4px
  color: $color-text-second
  font-size: 12px
  line-height: 1.3
</style>
